// src/App.tsx
//
// BekalNanti.ID — Consumer Web Shell
// Design System: Tunas Harapan visual direction
// Primary brand: BekalNanti.ID
// Workspace: Perencanaan
// Sections: Beranda | Orang | Rencana | Keuangan
// Signature Experience: PetaEsok

import React, { useState, useEffect, useCallback } from 'react'
import { Onboarding } from './onboarding/Onboarding'
import { MyJourney } from './journey/MyJourney'
import { BekalNantiLogo } from './components/BekalNantiLogo'
import { AuthModal, AuthMode } from './components/AuthModal'
import { MyProfileModal } from './components/MyProfileModal'
import { supabase } from './lib/supabase'
import { fetchJourneyData } from './lib/journeyService'
import {
  getCanonicalProfile,
  resolveDisplayName,
  getCachedProfileName,
  setCachedProfileName,
  clearCachedProfileName,
} from './lib/peopleService'
import { ProfileRecord } from './journey/types'
import { User } from '@supabase/supabase-js'
import { LogIn, LogOut, User as UserIcon, Compass } from 'lucide-react'

export type ViewMode = 'onboarding' | 'journey'

export const App: React.FC = () => {
  const [user, setUser] = useState<User | null>(null)
  const [userProfile, setUserProfile] = useState<ProfileRecord | null>(null)
  const [profileDisplayName, setProfileDisplayName] = useState<string | null>(null)
  const [viewMode, setViewMode] = useState<ViewMode>('onboarding')
  const [isAuthOpen, setIsAuthOpen] = useState(false)
  const [authMode, setAuthMode] = useState<AuthMode>('signin')
  const [hasJourney, setHasJourney] = useState(false)
  const [isProfileOpen, setIsProfileOpen] = useState(false)

  const loadUserProfile = useCallback(async (currentUser: User) => {
    // 1. Instant cache retrieval to prevent any display name flash
    const cached = getCachedProfileName(currentUser.id)
    if (cached) {
      setProfileDisplayName(cached)
    }

    try {
      // 2. Authoritative database query: bekalnanti_app.profiles
      const profile = await getCanonicalProfile(currentUser.id)
      if (profile && profile.displayName && profile.displayName.trim().length > 0) {
        setUserProfile(profile)
        setProfileDisplayName(profile.displayName.trim())
        setCachedProfileName(currentUser.id, profile.displayName.trim())

        // Background sync to GoTrue auth user metadata if different
        if (currentUser.user_metadata?.display_name !== profile.displayName.trim()) {
          try {
            await supabase.auth.updateUser({
              data: { display_name: profile.displayName.trim() },
            })
          } catch (syncErr) {
            console.warn('Background sync to auth metadata failed:', syncErr)
          }
        }
      } else {
        // Fallback priority: auth metadata > email
        const fallback = resolveDisplayName(
          null,
          currentUser.user_metadata?.display_name,
          currentUser.email
        )
        setProfileDisplayName(fallback)
      }
    } catch (err) {
      console.warn('Could not load canonical profile:', err)
      const fallback = resolveDisplayName(
        null,
        currentUser.user_metadata?.display_name,
        currentUser.email
      )
      setProfileDisplayName(fallback)
    }
  }, [])

  const checkUserJourney = useCallback(
    async (currentUser: User) => {
      await loadUserProfile(currentUser)
      try {
        const data = await fetchJourneyData(currentUser)
        if (data?.profile?.displayName) {
          setProfileDisplayName(data.profile.displayName)
          setUserProfile(data.profile)
          setCachedProfileName(currentUser.id, data.profile.displayName)
        }
        if (data && data.goals.length > 0) {
          setHasJourney(true)
          // Check URL params: if explicitly requesting onboarding, don't force journey
          const params = typeof window !== 'undefined' ? new URLSearchParams(window.location.search) : null
          const isExplicitOnboarding = params?.has('step') || params?.get('view') === 'onboarding'
          if (!isExplicitOnboarding) {
            setViewMode('journey')
          }
        } else {
          setHasJourney(false)
        }
      } catch (err) {
        console.warn('Could not check user journey:', err)
        setHasJourney(false)
      }
    },
    [loadUserProfile]
  )

  useEffect(() => {
    // 1. Get initial session
    supabase.auth.getSession().then(({ data: { session } }) => {
      const currentUser = session?.user ?? null
      setUser(currentUser)
      if (currentUser) {
        checkUserJourney(currentUser)
      }
    })

    // 2. Listen for auth state changes
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      const currentUser = session?.user ?? null
      setUser(currentUser)
      if (currentUser) {
        checkUserJourney(currentUser)
      } else {
        setUserProfile(null)
        setProfileDisplayName(null)
        setHasJourney(false)
        setViewMode('onboarding')
      }
    })

    return () => {
      subscription.unsubscribe()
    }
  }, [checkUserJourney])

  const handleLogout = async () => {
    if (user) {
      clearCachedProfileName(user.id)
    }
    await supabase.auth.signOut()
    setUser(null)
    setUserProfile(null)
    setProfileDisplayName(null)
    setHasJourney(false)
    setViewMode('onboarding')
  }

  const openAuth = (mode: AuthMode = 'signin') => {
    setAuthMode(mode)
    setIsAuthOpen(true)
  }

  const handleAuthSuccess = async () => {
    setIsAuthOpen(false)
    const {
      data: { session },
    } = await supabase.auth.getSession()
    const currentUser = session?.user ?? user
    if (currentUser) {
      setUser(currentUser)
      await checkUserJourney(currentUser)
    }
  }

  const handleNavigateToJourney = () => {
    setHasJourney(true)
    setViewMode('journey')
  }

  return (
    <div className="min-h-screen bg-[#F8FAFA] text-slate-900 flex flex-col font-sans selection:bg-[#CCFBF1] selection:text-[#0F766E] overflow-x-hidden">
      {/* Top Header */}
      <header className="sticky top-0 z-30 w-full bg-white/90 backdrop-blur-md border-b border-slate-200/80 shadow-2xs">
        <div className="max-w-md sm:max-w-xl mx-auto px-3 sm:px-4 h-14 flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 sm:gap-3 min-w-0">
            <BekalNantiLogo variant="horizontal" iconSize={30} />

            {/* View Mode Navigation Switcher for authenticated users with journey */}
            {user && hasJourney && (
              <div className="hidden sm:flex items-center gap-1 p-0.5 rounded-xl bg-slate-100 border border-slate-200/80 text-xs">
                <button
                  type="button"
                  onClick={() => setViewMode('journey')}
                  className={`px-2.5 py-1 rounded-lg font-semibold transition-all cursor-pointer flex items-center gap-1 ${
                    viewMode === 'journey'
                      ? 'bg-white text-[#0F766E] shadow-2xs'
                      : 'text-slate-500 hover:text-slate-800'
                  }`}
                >
                  <Compass size={12} className="text-[#0EA5A4]" />
                  <span>Perencanaan</span>
                </button>
                <button
                  type="button"
                  onClick={() => setViewMode('onboarding')}
                  className={`px-2.5 py-1 rounded-lg font-semibold transition-all cursor-pointer flex items-center gap-1 ${
                    viewMode === 'onboarding'
                      ? 'bg-white text-[#0F766E] shadow-2xs'
                      : 'text-slate-500 hover:text-slate-800'
                  }`}
                >
                  <Compass size={12} className="text-slate-400" />
                  <span>Onboarding</span>
                </button>
              </div>
            )}
          </div>

          {/* User state / Auth actions */}
          <div className="flex items-center gap-1.5 sm:gap-2 flex-shrink-0">
            {user ? (
              <div className="flex items-center gap-1 sm:gap-2">
                <button
                  type="button"
                  onClick={() => setIsProfileOpen(true)}
                  title="Kelola Profil Saya"
                  className="flex items-center gap-1.5 px-2 sm:px-2.5 py-1 rounded-full bg-teal-50 hover:bg-teal-100 border border-teal-200/70 text-[#0F766E] text-xs font-medium max-w-[80px] sm:max-w-[140px] truncate cursor-pointer transition-colors"
                >
                  <UserIcon size={13} className="text-[#0EA5A4] flex-shrink-0" />
                  <span className="truncate">
                    {profileDisplayName ||
                      resolveDisplayName(
                        userProfile?.displayName,
                        user.user_metadata?.display_name,
                        user.email
                      )}
                  </span>
                </button>
                <button
                  type="button"
                  onClick={handleLogout}
                  title="Keluar dari akun"
                  className="p-1.5 rounded-lg text-slate-400 hover:text-rose-600 hover:bg-rose-50 transition-colors cursor-pointer"
                  aria-label="Keluar"
                >
                  <LogOut size={16} />
                </button>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => openAuth('signin')}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-teal-50 hover:bg-teal-100 text-[#0F766E] font-semibold text-xs border border-teal-200/80 transition-all cursor-pointer shadow-2xs active:scale-95"
              >
                <LogIn size={14} className="text-[#0EA5A4]" />
                <span>Masuk</span>
              </button>
            )}
          </div>
        </div>
      </header>

      {/* Main Content Area — Mobile First */}
      <main className="flex-1 flex flex-col items-center justify-start px-4 pt-4 pb-16">
        <div className="w-full max-w-md sm:max-w-xl">
          {viewMode === 'journey' ? (
            <MyJourney
              user={user}
              onOpenAuth={openAuth}
              onReturnToOnboarding={() => setViewMode('onboarding')}
              isProfileOpenFromApp={isProfileOpen}
              onCloseProfileFromApp={() => setIsProfileOpen(false)}
              onProfileUpdated={(updated) => {
                setUserProfile(updated)
                setProfileDisplayName(updated.displayName)
                if (user) {
                  setCachedProfileName(user.id, updated.displayName)
                }
              }}
            />
          ) : (
            <Onboarding
              user={user}
              onOpenAuth={openAuth}
              onNavigateToJourney={handleNavigateToJourney}
            />
          )}
        </div>
      </main>

      {/* Footer */}
      <footer className="w-full border-t border-slate-200/60 bg-white py-6 px-4 text-center">
        <div className="max-w-md mx-auto space-y-1.5">
          <p className="text-xs font-semibold text-slate-700">
            BekalNanti.ID — Persiapan Hari Ini, Peluang Lebih Besar Nanti
          </p>
          <p className="text-[11px] text-slate-400">
            Platform Perencanaan Kehidupan Pribadi &amp; Keluarga • Indonesia
          </p>
        </div>
      </footer>

      {/* Authentication Modal */}
      <AuthModal
        isOpen={isAuthOpen}
        initialMode={authMode}
        onClose={() => setIsAuthOpen(false)}
        onSuccess={handleAuthSuccess}
      />

      {/* Global Profile Modal accessible from header */}
      {isProfileOpen && userProfile && (
        <MyProfileModal
          isOpen={isProfileOpen}
          profile={userProfile}
          userEmail={user?.email}
          onClose={() => setIsProfileOpen(false)}
          onUpdateSuccess={(updated) => {
            setUserProfile(updated)
            setProfileDisplayName(updated.displayName)
            if (user) {
              setCachedProfileName(user.id, updated.displayName)
            }
          }}
        />
      )}
    </div>
  )
}

export default App
